import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as transactionService from "@/shared/api/services/transactionService";
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import { motion } from "framer-motion";
import { Search, Filter, Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/components/ui/calendar";
import { format } from "date-fns";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function MerchantTransactions() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 15;

  const { data: transactions = [], isLoading: isLoadingTransactions } = useQuery({
    queryKey: ["merchant-transactions"],
    queryFn: () => transactionService.getMerchantTransactions(),
  });

  const { data: qrCodes = [], isLoading: isLoadingQRCodes } = useQuery({
    queryKey: ["merchant-qr-codes"],
    queryFn: () => qrCodeService.getMerchantQRCodes(),
  });

  const combinedData = React.useMemo(() => {
    const formattedQRCodes = qrCodes.map((qr) => ({ 
      id: qr.id,
      transaction_id: qr.payment_id,
      merchant_ref: qr.reference,
      type: "deposit", // Treat QR codes as deposits
      status: qr.status === "paid" || qr.status === "completed" ? "success" : qr.status,
      amount: qr.amount,
      provider_display: qr.client_bank_name || "-",
      currency: qr.currency,
      created_date: qr.createdAt,
      processed_at: qr.updatedAt, // Estimate
      completed_at: qr.updatedAt, // Estimate
      provider_code: "QR Code",
      payment_method: "PROMPTPAY_QR",
      is_qr_code: true,
    }));

    return [...transactions, ...formattedQRCodes].sort((a, b) =>
      new Date(b.created_date) - new Date(a.created_date)
    );
  }, [transactions, qrCodes]);

  const filteredTransactions = React.useMemo(() => {
    return combinedData.filter((item) => {
      if (search) {
        const searchLower = search.toLowerCase();
        if (
          !item.transaction_id?.toLowerCase().includes(searchLower) &&
          !item.merchant_ref?.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (typeFilter !== "all" && item.type !== typeFilter) return false;

      if (dateRange.from && new Date(item.created_date) < dateRange.from) return false;
      if (dateRange.to && new Date(item.created_date) > dateRange.to) return false;
      return true;
    });
  }, [combinedData, search, statusFilter, typeFilter, dateRange]);

  const paginatedTransactions = filteredTransactions.slice(
    page * pageSize,
    (page + 1) * pageSize
  );
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
    setDateRange({ from: null, to: null });
    setPage(0);
  };

  const hasFilters = search || statusFilter !== "all" || typeFilter !== "all" || dateRange.from;
  const isLoading = isLoadingTransactions || isLoadingQRCodes;

  return (
    <MerchantLayout currentPage="MerchantTransactions" merchantName="Royal Gaming" merchantCode="MRC001">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("transactions")}</h1>
          <p className="text-slate-500">{filteredTransactions.length} {t("transactionsFound")}</p>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-4 border border-slate-100"
        >
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("searchHeaderPlaceholder")}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="success">{t("success")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="processing">{t("processing")}</SelectItem>
                <SelectItem value="failed">{t("failed")}</SelectItem>
                <SelectItem value="expired">{t("expired")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select
              value={typeFilter}
              onValueChange={(v) => {
                setTypeFilter(v);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("type")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allTypes")}</SelectItem>
                <SelectItem value="deposit">{t("deposit")}</SelectItem>
                <SelectItem value="withdrawal">{t("withdrawal")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Range */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Calendar className="h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                    ) : (
                      format(dateRange.from, "MMM d")
                    )
                  ) : (
                    t("dateRange")
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range || { from: null, to: null });
                    setPage(0);
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {/* Clear Filters */}
            {hasFilters && (
              <Button variant="ghost" onClick={clearFilters} className="gap-1 text-slate-500">
                <X className="h-4 w-4" />
                {t("clearFilters")}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Table */}
        <TransactionTable
          transactions={paginatedTransactions}
          isLoading={isLoading}
          onRowClick={setSelectedTransaction}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {t("showing")} {page * pageSize + 1} {t("to")}{" "}
              {Math.min((page + 1) * pageSize, filteredTransactions.length)} {t("of")}{" "}
              {filteredTransactions.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">
                {t("page")} {page + 1} {t("of")} {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <TransactionDetailPanel
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />
    </MerchantLayout>
  );
}